import { ChevronRight } from 'lucide-react'

import type { PublicAppointment, PublicPatient, Region } from '@snootsvet/shared/portal'

import { AppointmentAttributes } from '@/customer/components/booking/appointment-attributes'
import { cn } from '@/customer/lib/utils'

/**
 * Card for the next upcoming appointment on the portal home.
 *
 * Composes appointment type + clinic labels from the shared `*_BY_ID`
 * lookups rather than requiring the backend to embed them in
 * `PublicAppointment`. If the appointment type or clinic id ever falls
 * outside those tables we render "Visit" / omit the location chip
 * rather than blowing up.
 *
 * Two-band layout: a yellow header strip labels the card ("Next
 * appointment") and a muted body carries the detail. Treated as a
 * single composite card - the horizontal colour split is internal to
 * one rounded rectangle rather than two rectangles touching, so we
 * stay inside DESIGN.md's mixed-corners rule via `overflow-hidden` on
 * the outer wrapper.
 */
export function UpcomingVisitCard({
  appointment,
  pet,
  region,
  onOpen,
  className,
}: {
  appointment: PublicAppointment
  pet: PublicPatient
  /** Locale + hour convention for the date/time chips. */
  region: Region
  onOpen?: () => void
  className?: string
}) {
  const start = new Date(appointment.startTime)
  const vetName = `${appointment.staff.firstName} ${appointment.staff.lastName.charAt(0)}.`

  // Treat the card as a clickable surface rather than a semantic
  // `<button>` - lets us use flow content inside (`<p>`, `<ul>`, ...)
  // and reads more honestly as "a piece of content that also opens on
  // tap" than as an action button. When `onOpen` is omitted the card
  // is plain / non-interactive; otherwise we spread role + tabIndex +
  // keyboard handling so Space / Enter still activate it.
  const interactiveProps = onOpen
    ? ({
        role: 'button',
        tabIndex: 0,
        onClick: onOpen,
        onKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            onOpen()
          }
        },
      } as const)
    : {}

  return (
    <div
      {...interactiveProps}
      className={cn(
        'group flex w-full flex-col overflow-hidden rounded-2xl border-2 border-foreground shadow-[6px_6px_0_0] shadow-yellow transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring cursor-pointer',
        className
      )}
    >
      <div className="flex items-center justify-between bg-yellow px-3 xs:px-4 py-2.5">
        <p className="text-button-sm">Next appointment</p>
        <ChevronRight
          className="size-5 shrink-0 transition-transform group-hover:translate-x-0.5"
          aria-hidden
        />
      </div>

      <div className="bg-muted p-3 xs:p-4">
        <AppointmentAttributes
          appointmentTypeId={appointment.appointmentTypeId}
          clinicId={appointment.clinicId}
          start={start}
          region={region}
          petName={pet.name}
          petSpecies={pet.species}
          vetName={vetName}
        />
      </div>
    </div>
  )
}
